import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSite } from '../context/SiteContext';

/**
 * SEO component to set per-page meta tags.
 *
 * Props:
 * - title, description, image, keywords (strings)
 * - canonical: pathname (e.g. "/shop") OR full URL (defaults to current path)
 * - type: "website" | "article" | "product"
 * - jsonLd: object or array of objects to inject as JSON-LD
 * - noIndex: if true, sets robots noindex
 */
export default function SEO({
    title,
    description,
    image,
    keywords,
    canonical,
    type = 'website',
    jsonLd,
    noIndex = false,
}) {
    const { settings } = useSite();
    const base = (settings?.site_url || 'https://byanfal.com').replace(/\/$/, '');
    const brand = settings?.brand_name || 'by anfal';

    const fullTitle = title
        ? (title.includes(brand) ? title : `${title} | ${brand}`)
        : (settings?.seo_default_title || `${brand} — Elegant, Effortless Style`);
    const desc = description || settings?.seo_default_description || "Luxury women's fashion atelier.";
    const og = image || settings?.seo_default_image || settings?.hero_image || settings?.logo_url || '';
    const kw = keywords || settings?.seo_keywords || '';
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const canonicalUrl = canonical
        ? (canonical.startsWith('http') ? canonical : `${base}${canonical}`)
        : `${base}${path}`;
    const twitterHandle = settings?.seo_twitter_handle || '';
    const gaId = settings?.ga_tracking_id || '';
    const googleVerify = settings?.google_site_verification || '';
    const bingVerify = settings?.bing_site_verification || '';

    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {kw && <meta name="keywords" content={kw} />}
            <meta name="author" content={settings?.seo_author || brand} />
            <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={brand} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={canonicalUrl} />
            {og && <meta property="og:image" content={og} />}
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            {og && <meta name="twitter:image" content={og} />}
            {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
            {twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}

            {/* Verification */}
            {googleVerify && <meta name="google-site-verification" content={googleVerify} />}
            {bingVerify && <meta name="msvalidate.01" content={bingVerify} />}

            {/* Google Analytics (GA4) */}
            {gaId && (
                <script async src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}></script>
            )}
            {gaId && (
                <script>{`
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${gaId}', { page_path: window.location.pathname });
                `}</script>
            )}

            {/* JSON-LD */}
            {jsonLdArray.map((obj, i) => (
                <script key={obj['@type'] || `jsonld-${i}`} type="application/ld+json">{JSON.stringify(obj)}</script>
            ))}
        </Helmet>
    );
}
